import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, Server, Loader, CheckCircle, XCircle, Shield, Lock, Wifi, Radio, Database, Zap, Activity } from 'lucide-react';

const S2SConnectionAnimation = ({ 
  recipientIp, 
  recipientPort, 
  onComplete, 
  transferData 
}) => {
  const [phase, setPhase] = useState('initiating');
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);

  const phases = {
    initiating: { text: 'INITIATING CONNECTION...', icon: Radio, color: 'text-blue-400', duration: 1000 },
    resolving: { text: 'RESOLVING DNS...', icon: Database, color: 'text-cyan-400', duration: 800 },
    handshake: { text: 'SSL/TLS HANDSHAKE...', icon: Lock, color: 'text-yellow-400', duration: 1200 },
    authenticating: { text: 'AUTHENTICATING...', icon: Shield, color: 'text-purple-400', duration: 1000 },
    establishing: { text: 'ESTABLISHING SECURE TUNNEL...', icon: Wifi, color: 'text-green-400', duration: 1500 },
    transferring: { text: 'TRANSFERRING DATA...', icon: Zap, color: 'text-terminal-green', duration: 2000 },
    verifying: { text: 'VERIFYING TRANSACTION...', icon: CheckCircle, color: 'text-green-500', duration: 800 },
    complete: { text: 'TRANSFER COMPLETE', icon: CheckCircle, color: 'text-terminal-green', duration: 500 }
  };

  React.useEffect(() => {
    let currentPhase = 0;
    const phaseKeys = Object.keys(phases);
    
    const runPhase = () => {
      if (currentPhase < phaseKeys.length) {
        const phaseName = phaseKeys[currentPhase];
        const phaseData = phases[phaseName];
        
        setPhase(phaseName);
        setProgress(((currentPhase + 1) / phaseKeys.length) * 100);
        
        // Add phase log
        setLogs(prev => [...prev, {
          time: new Date().toLocaleTimeString(),
          phase: phaseName,
          message: phaseData.text,
          color: phaseData.color
        }]);
        
        // Add detailed sub-logs
        setTimeout(() => {
          const subLogs = getSubLogs(phaseName, recipientIp, recipientPort);
          subLogs.forEach((log, index) => {
            setTimeout(() => {
              setLogs(prev => [...prev, {
                time: new Date().toLocaleTimeString(),
                phase: phaseName,
                message: log.message,
                color: 'text-gray-400',
                indent: true
              }]);
            }, index * 150);
          });
        }, 200);
        
        currentPhase++;
        setTimeout(runPhase, phaseData.duration + 500);
      } else {
        setTimeout(() => {
          onComplete(true, 'Transfer completed successfully');
        }, 1000);
      }
    };
    
    runPhase();
  }, []);

  const getSubLogs = (phase, ip, port) => {
    switch(phase) {
      case 'initiating':
        return [
          { message: `→ Target: ${ip}:${port}` },
          { message: `→ Protocol: S2S-SECURE-v2.0` },
          { message: `→ Encryption: AES-256-GCM` }
        ];
      case 'resolving':
        return [
          { message: `→ DNS Query: ${ip}` },
          { message: `→ Response: ${ip} (0ms)` },
          { message: `→ IPv4 resolved successfully` }
        ];
      case 'handshake':
        return [
          { message: `→ Sending Client Hello...` },
          { message: `→ Received Server Hello` },
          { message: `→ Certificate verified ✓` },
          { message: `→ Session key exchanged` }
        ];
      case 'authenticating':
        return [
          { message: `→ Sending credentials...` },
          { message: `→ JWT token validated` },
          { message: `→ Access granted ✓` }
        ];
      case 'establishing':
        return [
          { message: `→ Creating secure tunnel...` },
          { message: `→ Tunnel encrypted with AES-256` },
          { message: `→ Connection stable` }
        ];
      case 'transferring':
        return [
          { message: `→ Amount: $${transferData.amount} ${transferData.currency}` },
          { message: `→ Encoding transaction data...` },
          { message: `→ Sending encrypted payload...` },
          { message: `→ Packet transmission: 100%` },
          { message: `→ Acknowledgment received` }
        ];
      case 'verifying':
        return [
          { message: `→ Computing transaction hash...` },
          { message: `→ Hash: ${generateHash()}` },
          { message: `→ Signature verified ✓` }
        ];
      case 'complete':
        return [
          { message: `→ Transaction ID: ${generateTxId()}` },
          { message: `→ Status: SUCCESS` }
        ];
      default:
        return [];
    }
  };

  const generateHash = () => {
    return Array.from({ length: 64 }, () => 
      Math.floor(Math.random() * 16).toString(16)
    ).join('').toUpperCase();
  };

  const generateTxId = () => {
    return 'TX-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9).toUpperCase();
  };

  const PhaseIcon = phases[phase]?.icon || Terminal;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
      <div className="scanline" />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-4xl terminal-card p-8"
      >
        {/* Header */}
        <div className="mb-6 text-center">
          <motion.div
            animate={{ rotate: phase === 'transferring' ? 360 : 0 }}
            transition={{ duration: 2, repeat: phase === 'transferring' ? Infinity : 0, ease: 'linear' }}
            className="inline-block"
          >
            <PhaseIcon className={`w-16 h-16 mx-auto mb-4 ${phases[phase]?.color || 'text-terminal-green'}`} />
          </motion.div>
          <h2 className="text-3xl font-bold cyber-font glow-text mb-2">
            SERVER-TO-SERVER TRANSFER
          </h2>
          <p className="text-terminal-green-dim text-sm">
            {phases[phase]?.text || 'PROCESSING...'}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs mb-2">
            <span className="text-terminal-green-dim">Progress</span>
            <span className="text-terminal-green font-mono">{progress.toFixed(0)}%</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-terminal-green"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              style={{ boxShadow: '0 0 10px #00ff41' }}
            />
          </div>
        </div>

        {/* Connection Info */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="terminal-card p-4 border border-terminal-green/30">
            <div className="text-xs text-terminal-green-dim mb-1">TARGET</div>
            <div className="font-mono text-sm">{recipientIp}:{recipientPort}</div>
          </div>
          <div className="terminal-card p-4 border border-terminal-green/30">
            <div className="text-xs text-terminal-green-dim mb-1">AMOUNT</div>
            <div className="font-mono text-sm">${transferData.amount} {transferData.currency}</div>
          </div>
          <div className="terminal-card p-4 border border-terminal-green/30">
            <div className="text-xs text-terminal-green-dim mb-1">SECURITY</div>
            <div className="font-mono text-sm text-green-400">AES-256</div>
          </div>
        </div>

        {/* Logs */}
        <div className="terminal-card p-4 h-64 overflow-y-auto terminal-font text-xs">
          {logs.map((log, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              className={`${log.color} ${log.indent ? 'ml-4' : ''} mb-1`}
            >
              <span className="text-gray-500">[{log.time}]</span> {log.message}
            </motion.div>
          ))}
        </div>

        {/* Particle Effects */}
        {phase === 'transferring' && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 20 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-terminal-green rounded-full"
                initial={{ 
                  x: '50%', 
                  y: '50%',
                  opacity: 1 
                }}
                animate={{ 
                  x: `${Math.random() * 100}%`,
                  y: `${Math.random() * 100}%`,
                  opacity: 0
                }}
                transition={{ 
                  duration: Math.random() * 2 + 1,
                  repeat: Infinity,
                  delay: Math.random() * 2
                }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default S2SConnectionAnimation;
